import React, { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";

import FormField from "../../../components/form-field/FormField";
import SuggestionBox from "../../../components/suggestion-box/SuggestionBox";
import { NameEntity } from "../../../helpers/NameEntity";
import UIObject from "../../../models/UIObject";
import { ProjectFormatResponse } from "../../../api-models/response/ProjectFormatResponse";
import { ProjectCodeSystemResponse } from "../../../api-models/response/ProjectCodeSystemResponse";
import { translationService } from "../../../services/translation/TranslationService";
import { RequiredValidatorModel } from "../../../services/form/validators/required/RequiredValidator";
import { UsersStorageModel } from "../../../services/user/UsersStorage";
import { api } from "../../../services/api/ApiService";
import { DropdownOption } from "../../../components/dropdown/Dropdown";
import {
  TimeframeValidatorModel,
  TimeframeDateModel,
} from "../../../services/form/validators/date/TimeframeValidator";
import DatePicker from "../../../components/datepicker/DatePicker";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import { ProjectStatus } from "../../../generated/Projects";

interface ProjectNameProps {
  name: string;
}

export function DisplayProjectName(props: ProjectNameProps) {
  return (
    <FormField
      id="project-name"
      labelKey="general.projects.name"
      readOnly
      fieldValue={props.name}
    />
  );
}

export function EditProjectName(props: ProjectNameProps) {
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  return (
    <FormField
      id="project-name"
      className="input"
      labelKey="general.projects.name"
      required
      component="input"
      fieldName="name"
      fieldValue={props.name}
      validator={requiredValidator}
      placeholderKey="projects.EditProjectName.placeholder"
      maxLength={100}
      errorName="name"
    />
  );
}

interface ProjectFormatProps {
  format: ProjectFormatResponse;
}

export function DisplayProjectFormat(props: ProjectFormatProps) {
  return (
    <FormField
      id="project-format"
      labelKey="general.project.format"
      readOnly
      fieldValue={props.format.id ? props.format.name : "-"}
    />
  );
}

interface ProjectCodeSystemProps {
  codeSystem: ProjectCodeSystemResponse;
}

export function DisplayProjectCodeSystem(props: ProjectCodeSystemProps) {
  return (
    <FormField
      id="project-codeSystem"
      labelKey="general.project.codeSystem"
      readOnly
      fieldValue={props.codeSystem.id ? props.codeSystem.name : "-"}
    />
  );
}

interface DisplayProjectStatusProps {
  status: number;
}

export const DisplayProjectStatus = observer((props: DisplayProjectStatusProps) => {
  return (
    <FormField
      id="status"
      labelKey="ViewProject.status"
      readOnly
      fieldValue={translationService.translate(
        `general.projects.status.${ProjectStatus[props.status]}`
      )}
    />
  );
});

interface EditProjectStatusProps {
  status: DropdownOption;
  onChange?: (status: ProjectStatus) => void;
  setFieldValue: Function;
}

export const EditProjectStatus = observer((props: EditProjectStatusProps) => {
  const { status, onChange, setFieldValue } = props;

  const projectStatuses = Object.keys(ProjectStatus)
    .filter((x) => isNaN(Number(x)))
    .map((key) => {
      return {
        id: ProjectStatus[key],
        name: translationService.translate(`general.${key.toLowerCase()}`),
      };
    });

  return (
    <FormField
      id="option"
      className="select"
      labelKey="projects.EditProjectStatus.label"
      required
      fieldName="status"
      fieldValue={status}
      errorName="status"
      children={() => (
        <Dropdown
          selectedOption={status}
          options={projectStatuses}
          onChange={onChange}
          fieldName="status"
          setFieldValue={setFieldValue}
        />
      )}
    />
  );
});

interface DisplayProjectLeadProps {
  lead: NameEntity;
}

export function DisplayProjectLead(props: DisplayProjectLeadProps) {
  return (
    <FormField
      id="project-lead"
      labelKey="general.projects.lead"
      readOnly
      fieldValue={props.lead.name}
    />
  );
}

interface DisplayReportTemplateProps {
  template: NameEntity;
}

export function DisplayReportTemplate(props: DisplayReportTemplateProps) {
  return (
    <FormField
      id="project-report-template"
      labelKey="general.project.report-template"
      readOnly
      fieldValue={props.template?.name ?? "-"}
    />
  );
}

interface EditProjectLeadProps {
  lead: UIObject;
  leadId: number;
  setFieldValue: Function;
  disabled?: boolean;
}

export function EditProjectLead(props: EditProjectLeadProps) {
  const { setFieldValue } = props;

  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  const [usersService] = useState(() => UsersStorageModel.create({}, { api }));

  const handleSearchUserChange = async (searchText: string) => {
    return await usersService.getFilteredUsersExcludingGuest(searchText);
  };

  const changeLead = (user: NameEntity | undefined) => {
    const id = user ? user.id : "";
    const name = user ? user.name : "";
    setFieldValue("lead", { id, name });
    setFieldValue("leadId", id);
  };

  const clearProjectLeadFieldValue = (text: string) => {
    if (!text) {
      setFieldValue("lead", '');
      setFieldValue("leadId", '');
    }
  };

  return (
    <FormField
      id="project-lead"
      labelKey="general.projects.lead"
      fieldName="leadId"
      fieldValue={props.leadId}
      validator={requiredValidator}
      required
      children={() => (
        <SuggestionBox
          defaultSelectedItem={props.lead.id ? props.lead : undefined}
          onSearchTextChange={(searchText) => handleSearchUserChange(searchText)}
          searchTextChanged={clearProjectLeadFieldValue}
          selectedItemChanged={changeLead}
          placeholderKey="projects.EditProjectLead.suggestion-placeholder"
          noResultsFoundKey="general.users.not-found"
          disabled={props.disabled}
          clearCurrentItem={() => changeLead(undefined)}
        />
      )}
      errorName="leadId"
    />
  );
}

interface EditAvailableFromAndDeadlineDatesProps {
  availableFrom: Date;
  deadline: Date;
  setFieldValue: Function;
}

export function EditAvailableFromAndDeadlineDates(props: EditAvailableFromAndDeadlineDatesProps) {
  const { setFieldValue } = props;

  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));
  const [timeframeValidator] = useState(() =>
    TimeframeValidatorModel.create(
      {},
      {
        translation: translationService,
      }
    )
  );

  const changeAvailableFromDate = useCallback(
    (date: any) => {
      setFieldValue("availableFrom", date);
    },
    [setFieldValue]
  );

  const changeDeadlineDate = useCallback(
    (date: any) => {
      const startDate = TimeframeDateModel.create({
        date: props.availableFrom,
        fieldName: "general.available-from",
      });
      const endDate = TimeframeDateModel.create({
        date: date ? date : null,
        fieldName: "general.deadline",
      });

      setFieldValue("deadline", date);
      timeframeValidator.setTimeFrame(startDate, endDate);
    },
    [props, timeframeValidator, setFieldValue]
  );

  useEffect(() => {
    const startDate = TimeframeDateModel.create({
      date: props.availableFrom,
      fieldName: "general.available-from",
    });
    const endDate = TimeframeDateModel.create({
      date: props.deadline ? props.deadline : null,
      fieldName: "general.deadline",
    });
    timeframeValidator.setTimeFrame(startDate, endDate);
  }, [props, setFieldValue, timeframeValidator]);

  return (
    <>
      <FormField
        id="available-from"
        labelKey="general.available-from"
        required
        fieldName="availableFrom"
        fieldValue={props.availableFrom}
        validator={requiredValidator}
        component={() => (
          <DatePicker
            isRequired
            value={props.availableFrom}
            onChange={(date) => changeAvailableFromDate(date)}
            placeholderKey="EditAvailableFrom.add"
          />
        )}
        errorName="availableFrom"
      />
      <FormField
        id="deadline"
        labelKey="general.deadline"
        fieldName="deadline"
        fieldValue={props.deadline}
        component={() => (
          <DatePicker
            value={props.deadline}
            onChange={(date) => changeDeadlineDate(date)}
            placeholderKey="EditDeadline.add"
          />
        )}
        validator={timeframeValidator}
        errorName="deadline"
      />
    </>
  );
}
